import type { ULsLogger, LoggingArgs } from "@1js/office-online-telemetry";
import {
  LogCategory,
  TraceLevel,
  LogType,
  ConsoleLogger,
} from "@1js/office-online-telemetry";
import type { LoggerConfig } from "./loggerConfig";
import { bUlsCache } from "./bUlsCache";
import {
  updateSuppressedTagsFromSettings,
  isTagSuppressed,
} from "./suppressedTags";
import type {
  SettingsChangeListener,
  SettingsChangedEvent,
} from "../settings-manager";
import { SettingIds } from "../settings-manager";
import {
  getRelativeTimeSinceNavigationStart,
  getAbsoluteTimeSinceEpoch,
} from "../utils";

export { LogCategory, TraceLevel };

// TODO 3846361: move AdaptiveLogger to office-online-telemetry package
export interface AdaptiveLogger {
  ULS: ULsLogger;
  configure: (config: LoggerConfig) => void;
  settingsChangeHandler: SettingsChangeListener;
  getTimeSpentLogging: () => number;
  getUserSessionId: () => string;
  flushAndSetNewEndpoint: (
    remoteUrlEndpoint: string,
    onComplete?: () => void
  ) => void;
  updateEndpointAndFlush: (remoteUrlEndpoint: string) => void;
}

const DEFAULT_UPLOAD_CADENCE_IN_MS = 60000; // 1 minute

/**
 * This logger has the ability to log without a remote uls endpoint.
 * For debug version, it holds a ConsoleLogger inside.
 */
export function createAdaptiveLogger(config?: LoggerConfig): AdaptiveLogger {
  const cache = new bUlsCache(config);
  let consoleLogger: ConsoleLogger;

  if (process.env.NODE_ENV !== "production") {
    consoleLogger = new ConsoleLogger();
  }

  function traceTagImpl(
    tag: number,
    category: LogCategory,
    level: TraceLevel,
    message: string
  ): void {
    const start = getRelativeTimeSinceNavigationStart();
    if (isTagSuppressed(tag)) {
      return;
    }

    const args: LoggingArgs = {
      timestamp: getAbsoluteTimeSinceEpoch(),
      category,
      tag,
      level,
      type: LogType.Trace,
      message,
      correlationId: "",
    };
    cache.addLoggingTime(getRelativeTimeSinceNavigationStart() - start);
    cache.handleTrace(args);
  }

  return {
    ULS: {
      sendTraceTag: (
        tag: number,
        category: LogCategory,
        level: TraceLevel,
        message: string
      ) => {
        if (process.env.NODE_ENV !== "production") {
          consoleLogger.sendTraceTag(tag, category, level, message);
        }

        traceTagImpl(tag, category, level, message);
      },
      shipAssertTag: (
        tag: number,
        category: LogCategory,
        condition: boolean,
        message: string
      ) => {
        if (process.env.NODE_ENV !== "production") {
          consoleLogger.shipAssertTag(tag, category, condition, message);
        }

        if (condition) {
          traceTagImpl(tag, category, TraceLevel.Error, message);
        }
      },
      debugAssertTag: (
        tag: number,
        category: LogCategory,
        condition: boolean,
        message: string
      ) => {
        if (process.env.NODE_ENV !== "production") {
          consoleLogger.debugAssertTag(tag, category, condition, message);
        }

        // We don't yet have support for debug asserts. To do this properly, we want to write a TypeScript or Babel
        // plugin that does this transformation debugAssertTag(args) into node.env.production !== "production" && debugAssertTag(args)
        // to ensure that expensive conditionals are not evaluated in ship code.
        throw new Error(
          "Debug Asserts not Implemented in the TypeScript bULS module"
        );
      },
      flush: cache.flush,
      flushOnClose: cache.flushOnClose,
      setCorrelationId: () => {},
    },
    configure: (config: LoggerConfig) => {
      cache.configure(config);
    },
    settingsChangeHandler: (event: SettingsChangedEvent) => {
      // suppressed tags have changed or it is the first fetch - parse the settings
      updateSuppressedTagsFromSettings(
        event.settingsManager.getValue(
          SettingIds.setting_RemoteUlsSuppressions,
          ""
        ),
        event.settingsManager.getValue(SettingIds.setting_RemoteUlsETag, "")
      );

      cache.configure({
        uploadCadenceInMs: event.settingsManager.getValue(
          SettingIds.setting_UlsUploadCadenceMs,
          DEFAULT_UPLOAD_CADENCE_IN_MS
        ),
      });

      const newBulsEndpoint = event.settingsManager
        .getValue(SettingIds.setting_BulsEndpointUrl, "")
        .toString();
      if (newBulsEndpoint !== "") {
        cache.flushAndSetNewEndpoint(newBulsEndpoint);
      }
    },
    getTimeSpentLogging: () => cache.getTimeSpentLogging(),
    getUserSessionId: () => cache.getUserSessionId(),
    flushAndSetNewEndpoint: (
      remoteUrlEndpoint: string,
      onComplete?: () => void
    ) => {
      cache.flushAndSetNewEndpoint(remoteUrlEndpoint, onComplete);
    },
    updateEndpointAndFlush: (remoteUrlEndpoint: string) => {
      cache.updateEndpointAndFlush(remoteUrlEndpoint);
    },
  };
}
